import type { LivePreviewStatus, SandboxRuntimeStatus } from '@/stores/preview';
import type { SandboxStatus } from '@/stores/project/types';

const CONFLICT_CODE = 100007;
const PROJECT_SANDBOX_NOT_FOUND_CODE = 300008;
const PROJECT_SANDBOX_UNAVAILABLE_CODE = 400008;
const REQUEST_TIMEOUT_CODES = new Set(['ECONNABORTED', 'ETIMEDOUT']);
const PRESERVABLE_SANDBOX_RUNTIME_STATUSES = new Set<SandboxRuntimeStatus>([
  'booting',
  'live',
  'recovering',
]);

interface SandboxRuntimeState {
  sandboxId: string;
  sandboxUrl: string;
  livePreviewStatus: LivePreviewStatus;
  sandboxRuntimeStatus: SandboxRuntimeStatus;
}

interface SandboxRuntimeReuseState extends SandboxRuntimeState {
  sandboxStatus: SandboxStatus;
}

interface ReadySandboxPreviewState extends SandboxRuntimeState {
  sandboxIframeReady: boolean;
}

function hasReusableSandboxIdentity(state: SandboxRuntimeReuseState): boolean {
  return state.sandboxStatus === 'available'
    && Boolean(state.sandboxId)
    && Boolean(state.sandboxUrl)
    && state.livePreviewStatus !== 'error';
}

export function shouldReuseSandboxRuntime(state: SandboxRuntimeReuseState): boolean {
  return hasReusableSandboxIdentity(state) && state.sandboxRuntimeStatus === 'live';
}

export function shouldPreserveSandboxPreview(state: SandboxRuntimeReuseState): boolean {
  return hasReusableSandboxIdentity(state)
    && PRESERVABLE_SANDBOX_RUNTIME_STATUSES.has(state.sandboxRuntimeStatus);
}

/**
 * 后台 ensure 只要当前 live iframe 仍然可见且 ready，就不能清空它。
 * sandboxStatus 可能因心跳/页面初始化短暂回到 unknown，但 iframe 本身仍是可用的。
 */
export function shouldPreserveReadySandboxPreview(state: ReadySandboxPreviewState): boolean {
  return Boolean(state.sandboxId)
    && Boolean(state.sandboxUrl)
    && state.livePreviewStatus === 'ready'
    && state.sandboxRuntimeStatus === 'live'
    && state.sandboxIframeReady;
}

function getRequestCode(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const code = (error as { code?: unknown }).code;
  return typeof code === 'number' ? code : undefined;
}

export function isProjectSandboxConflictError(error: unknown): boolean {
  return getRequestCode(error) === CONFLICT_CODE;
}

export function isProjectSandboxNotFoundError(error: unknown): boolean {
  return getRequestCode(error) === PROJECT_SANDBOX_NOT_FOUND_CODE;
}

export function isProjectSandboxUnavailableError(error: unknown): boolean {
  return getRequestCode(error) === PROJECT_SANDBOX_UNAVAILABLE_CODE;
}

export function isProjectSandboxTimeoutError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const code = (error as { code?: unknown }).code;
  return typeof code === 'string' && REQUEST_TIMEOUT_CODES.has(code);
}
